import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap } from 'lucide-react';
import WeatherAnimations from '../components/WeatherAnimations';
import WeatherWidget from '../components/WeatherWidget';
import { useWeather } from '../hooks/useWeather';
import { sites, weatherConfig } from '../config/sites';

const Home = () => {
  const { weather, weatherTestMode } = useWeather();

  // Set page title
  useEffect(() => {
    document.title = 'Bethel Solar Dashboard';
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 p-6 relative">
      {/* Weather Animations */}
      <WeatherAnimations weather={weather} weatherTestMode={weatherTestMode} />

      {/* Test Mode Indicator */}
      {weatherTestMode && (
        <div className="fixed bottom-4 left-4 bg-black/80 text-white px-4 py-2 rounded-lg z-50 text-sm">
          Test Mode: {weatherTestMode} (Press 'W' to cycle)
        </div>
      )}

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-12 relative">
          {/* Weather Widget */}
          <WeatherWidget
            weather={weather}
            weatherTestMode={weatherTestMode}
            locationName={weatherConfig.location.name}
          />

          <div className="flex items-center justify-center gap-3 mb-4">
            <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent pb-1">
              Bethel Solar Dashboard
            </h1>
          </div>
          <p className="text-gray-600 text-xl mb-2">Bethel School District Solar Monitoring</p>
          <p className="text-gray-500">Select a site to view real-time solar generation data</p>
        </div>

        {/* Site Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
          {sites.map((site) => (
            <Link
              key={site.id}
              to={site.path}
              className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-200 overflow-hidden transform hover:-translate-y-1 flex flex-col"
            >
              <div className={`bg-gradient-to-r ${site.color} p-6 h-36 flex flex-col`}>
                <div className="flex items-center justify-between mb-3">
                  <Zap className="w-10 h-10 text-white" />
                  <span className="text-sm font-medium text-white bg-white bg-opacity-20 px-3 py-1 rounded-full">
                    {site.type === 'egauge' ? 'eGauge' : site.type === 'enphase' ? 'Enphase' : 'SolarEdge'}
                  </span>
                </div>
                <h2 className="text-2xl font-bold text-white leading-tight">{site.name}</h2>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <p className="text-gray-600 mb-4 flex-1">{site.description}</p>
                <div className="flex items-center text-blue-600 group-hover:text-blue-700 font-medium">
                  View Dashboard
                  <ArrowRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* Info Section */}
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">About Our Solar Program</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Real-Time Monitoring</h3>
              <p className="text-gray-600">
                Our solar monitoring systems provide real-time data updates every minute, giving you
                up-to-date visibility into solar energy generation across all Bethel School District facilities.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-700 mb-2">Environmental Impact</h3>
              <p className="text-gray-600">
                Track the environmental benefits of our solar installations, including equivalent homes powered,
                trees planted, and CO2 emissions reduced through clean energy generation.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Subtle credit in bottom right */}
      <div className="fixed bottom-2 right-2 text-xs text-gray-400 opacity-30 hover:opacity-70 transition-opacity pointer-events-none z-50">
        Designed and Built by Cory Fiala and Claude.AI
      </div>
    </div>
  );
};

export default Home;
